import Cropper from 'react-easy-crop'
import { cn } from '@/lib/utils'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { useAvatarUploader } from '@/features/settings/hooks/use-avatar-uploader'
import { AVATAR_ACCEPT } from '@/features/settings/lib/avatar-uploader-utils'

type AvatarUploaderProps = {
  initialUrl?: string | null
  onUploaded?: (url: string) => void
}

export function AvatarUploader({
  initialUrl,
  onUploaded,
}: AvatarUploaderProps) {
  const {
    avatarUrl,
    dialogOpen,
    previewUrl,
    crop,
    zoom,
    busy,
    canConfirm,
    uploadButtonText,
    inputRef,
    setCrop,
    setZoom,
    openFilePicker,
    closeDialog,
    handleDialogChange,
    handleFileChange,
    handleConfirm,
    handleCropComplete,
  } = useAvatarUploader({ initialUrl, onUploaded })

  return (
    <div className='space-y-2'>
      <div className='text-sm font-medium'>头像</div>
      <div className='flex items-center gap-4'>
        <div className='group'>
          <Avatar
            className={cn(
              'h-16 w-16 border transition',
              busy
                ? 'opacity-60'
                : 'group-hover:ring-primary group-hover:ring-2'
            )}
          >
            {avatarUrl ? (
              <AvatarImage src={avatarUrl} alt='avatar' />
            ) : (
              <AvatarFallback className='text-xs'>暂无</AvatarFallback>
            )}
          </Avatar>
        </div>
        <div className='flex flex-col gap-2'>
          <input
            ref={inputRef}
            type='file'
            accept={AVATAR_ACCEPT}
            className='hidden'
            onChange={handleFileChange}
            disabled={busy}
          />
          <Button
            type='button'
            variant='outline'
            onClick={openFilePicker}
            disabled={busy}
          >
            {uploadButtonText}
          </Button>
          <div className='text-muted-foreground text-xs'>
            支持 jpg / jpeg / png / gif，最大 2MB
          </div>
        </div>
      </div>

      <Dialog open={dialogOpen} onOpenChange={handleDialogChange}>
        <DialogContent className='sm:max-w-[900px]' showCloseButton={!busy}>
          <DialogHeader>
            <DialogTitle>裁剪头像</DialogTitle>
            <DialogDescription>拖动调整位置，滑条缩放。</DialogDescription>
          </DialogHeader>
          <div className='grid gap-4 md:grid-cols-[1fr_220px]'>
            <div className='bg-muted/20 relative aspect-square w-full overflow-hidden rounded-lg border'>
              {previewUrl && (
                <Cropper
                  image={previewUrl}
                  crop={crop}
                  zoom={zoom}
                  aspect={1}
                  cropShape='round'
                  showGrid={false}
                  onCropChange={setCrop}
                  onZoomChange={setZoom}
                  onCropComplete={handleCropComplete}
                />
              )}
            </div>

            <div className='space-y-4'>
              <div>
                <div className='text-muted-foreground text-xs'>缩放</div>
                <input
                  aria-label='缩放'
                  type='range'
                  min={1}
                  max={3}
                  step={0.01}
                  value={zoom}
                  onChange={(event) => setZoom(Number(event.target.value))}
                  className='w-full'
                  disabled={busy}
                />
              </div>

              <DialogFooter className='flex-col items-stretch'>
                <Button
                  type='button'
                  onClick={handleConfirm}
                  disabled={!canConfirm}
                >
                  {busy ? '处理中...' : '确认上传'}
                </Button>
                <Button
                  type='button'
                  variant='outline'
                  onClick={closeDialog}
                  disabled={busy}
                >
                  取消
                </Button>
              </DialogFooter>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  )
}
